import BaseClient from '@mapbox/mapbox-sdk';
import StaticClient from '@mapbox/mapbox-sdk/services/static';
import { FeatureCollection, Geometry } from 'geojson';

import { MapboxPublicToken } from '@/globals';
import { RateLimiter } from '@/utils/RateLimiter';

const BASE_URL = 'https://api.mapbox.com';

enum MapboxSearchCriteria {
  Address = 'address_line1',
  Street = 'street',
  AddressNumber = 'address_number',
  Block = 'block',
  Place = 'place',
  Region = 'region',
  Postcode = 'postcode',
  Locality = 'locality',
  Neighborhood = 'neighborhood',
  Country = 'country',
  TextQuery = 'q',
}

enum MapBoxContextType {
  Address = 'address',
  Street = 'street',
  Neighborhood = 'neighborhood',
  Postcode = 'postcode',
  Place = 'place',
  District = 'district',
  Region = 'region',
  Country = 'country',
}

type MapboxContextObject = { mapbox_id: string; name: string };

/**
 * Geocoding response object
 * @see https://docs.mapbox.com/api/search/geocoding/#geocoding-response-object
 */
type MapboxGeojsonProperties = {
  mapbox_id: string;
  feature_type: string;
  name: string;
  name_preferred: string;
  place_formatted: string;
  full_address: string;
  context: Record<MapBoxContextType, MapboxContextObject> & {
    [MapBoxContextType.Country]: MapboxContextObject & { country_code: string };
    [MapBoxContextType.Region]: MapboxContextObject & { region_code: string };
  };
  coordinates: {
    latitude: number;
    longitude: number;
    accuracy: string;
    routable_points;
  };
  bbox: [number, number, number, number]; // [minLon, minLat, maxLon, maxLat]
  match_code;
};

type MapboxGeocodeFeatureCollection = FeatureCollection<Geometry, MapboxGeojsonProperties>;

const RATE_LIMIT = 60; // 1000 requests per minute

export class MapboxApi {
  private static instance: MapboxApi;

  private staticClient: StaticClient;

  private rateLimiter: RateLimiter;

  private constructor() {
    const baseClient = BaseClient({ accessToken: MapboxPublicToken });
    this.staticClient = StaticClient(baseClient);
    this.rateLimiter = new RateLimiter(RATE_LIMIT);
  }

  public static getInstance() {
    if (!this.instance) {
      this.instance = new MapboxApi();
    }

    return this.instance;
  }

  public getStaticMapUrlForGeojson(geojson, position = 'auto', width = 320, height = 240, styleId = 'light-v9') {
    return this.staticClient
      .getStaticImage({
        ownerId: 'mapbox',
        styleId,
        width,
        height,
        position,
        overlays: [
          {
            geoJson: geojson,
          },
        ],
        highRes: true,
        attribution: false,
      })
      .url(MapboxPublicToken);
  }

  private buildRateLimitedRequest<T>(request: Request): Promise<T> {
    return this.rateLimiter.run(async () => {
      const response = await fetch(request);
      const data = await response.json();
      return data as T;
    });
  }

  /**
   * Perform a geocode search using the Mapbox API.
   * @param criterion
   * @returns
   * @see https://docs.mapbox.com/api/search/geocoding/#forward-geocoding-with-structured-input
   */
  public async forwardGeocode(
    criterion: Partial<Record<MapboxSearchCriteria, string>>,
  ): Promise<MapboxGeocodeFeatureCollection> {
    const searchParams = Object.entries(criterion)
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
    const url = `${BASE_URL}/search/geocode/v6/forward?${searchParams}&access_token=${MapboxPublicToken}`;
    return this.buildRateLimitedRequest(new Request(url));
  }
}
